import { html } from "htm/preact"
import { useState } from "preact/hooks"

import { get } from "./http"
import { useDateTime, useLocalStorage } from "./hooks"

// Debounce is in milliseconds so a 1hr cooldown
//  on a rule activation would be 1000 * 60 * 60
export const triggerGamificationRule = (actionName, debounce = 0) => {
	const eventId = window.dataLayer[0]["Event ID"]
	const regId = window.dataLayer[0]["Registrant ID"]
	const firstName = window.dataLayer[0]["First Name"]
	const lastName = window.dataLayer[0]["Last Name"]
	const email = window.dataLayer[0]["Email Address"]
	const optedIn = window.dataLayer[0]["I would like to opt in to the Leaderboard competition"] === "Yes"
	const lastTriggered = window.localStorage.getItem(`rule-${actionName}`) || "2021"
	if (new Date() < new Date(lastTriggered / 1 + debounce)) {
		return false
	} else {
		window.localStorage.setItem(`rule-${actionName}`, new Date().valueOf())
	}

	const registrantId = `${regId}_${firstName}_${lastName}_${email}`

	const query = { actionName, eventId, registrantId, swoogoAccess: false }

	if (optedIn) {
		get("https://sponsor-gamification.herokuapp.com/dynamic", query)
	}
}

export const TimeSwitch = ({ children }) => {
	const now = useDateTime("minute")

	return children.find(child =>
		new Date(child.starts) < now
		&& new Date(child.ends) > now
	)?.content || children[0].content || <h1>Something went wrong. If you feel helpful, please refresh the page and let the helpdesk know!</h1>
}

export const DisplayOnce = ({ id, children, ...rest}) => {
	const [isDisplayed, setIsDisplayed] = useLocalStorage(`displayOnce-${id}`)
	if (!isDisplayed) {
		return <Modal {...rest} key={`${id}-modal`} dismiss={() => setIsDisplayed(true)}>{children}</Modal>
	} else {
		return null
	}
}

export const DisplayAlways = ({ id, children, ...rest}) => {
	const [isDisplayed, setIsDisplayed] = useState(false)
	if (!isDisplayed) {
		return <Modal {...rest} key={`${id}-modal`} dismiss={() => setIsDisplayed(true)}>{children}</Modal>
	} else {
		return null
	}
}


export const Background = ({ imgURL, style }) =>
	imgURL.match(/\.mp4$/)
		? html`<video
		key=${imgURL}
		style="position: absolute; left: 0; width: 100%; top: 0; height: 100%; ${style}"
		id="main-video"
		class="tk-background"
		muted
		loop
		autoplay
	>
		<source src=${imgURL} />
	</video>`
		: html`<img
		key=${imgURL}
		id="main-image"
		class="tk-background"
		src=${imgURL}
		style=${`width: 100%; ${style}`}
	/>`

export const OpenCometChatGroup = () =>
	function openChat(name) {
		CometChatWidget.openOrCloseChat(true);
		CometChatWidget.chatWithGroup(name);
	}


export const Modal = ({ children, style = {}, dismiss, dismissHTML = null, dismissStyle }) => html`
	<div
		class="tk-modal-backdrop"
		style=${{
			background: "rgba(255, 255, 255, 0.5)",
			position: "fixed",
			left: 0,
			top: 0,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: "100%",
			height: "100%",
			zIndex: 100,
			overflow: "auto",
		}}
	>
		<div
			class="tk-modal-container"
			style=${{
				position: "relative",
				maxWidth: "100%",
				maxHeight: "100%",
				...style
			}}
		>
			${children}
			<a
				class="tk-modal-close"
				style=${{
					cursor: "pointer",
					position: "absolute",
					right: "11px",
					top: 0,
					fontSize: "40px",
					textAlign: "right",
					textDecoration: "none",
					...dismissStyle
				}}
				onClick=${dismiss}
			>${dismissHTML}<//>
		<//>
	<//>
`

export const ElfsightPopup = ({ trigger, ID }) => html`
	${trigger && html`<div key=${ID} id=${trigger} />`}
	<div class="elfsight-app-${ID}" />
`

export const Link = ({
	href = null,
	target = null,
	onClick = () => null,
	width,
	height,
	left,
	top,
	label,
	title = "",
	elfsightPopupID = null,
	modal = null,
	roomName = "",
	CometChatID = null,
	CometChatUserID = null,
	flyzooChatName = "unnamed",
	children = null,
	position = "absolute",
	isExternal = undefined,
	gameRule = null,
	gameRuleCooldown = 0,
	modalOverrides = {},
	gaAction = "Link Click",
	hoverMessage = null
}) => {
	const optionals = {}
	const [modalVisible, toggleModal] = useState(false)
	const isRedirectTrackingLink = !isExternal && href?.length && !href.startsWith("http") && !href.startsWith("/")
	let generatedOnClick = () => { }

	if (modal) {
		// eslint-disable-next-line no-param-reassign
		generatedOnClick = () => {
			toggleModal(!modalVisible)
		}
	} else if (CometChatID) {
		// eslint-disable-next-line no-param-reassign
		generatedOnClick = () => {
			window.CometChatWidget.openOrCloseChat(true)
			window.CometChatWidget.chatWithGroup(CometChatID)
		}
	} else if (CometChatUserID) {
		// eslint-disable-next-line no-param-reassign
		generatedOnClick = () => {
			window.CometChatWidget.openOrCloseChat(true)
			window.CometChatWidget.chatWithUser(CometChatUserID)
		}
	} else if (isRedirectTrackingLink) {
		// eslint-disable-next-line no-param-reassign
		generatedOnClick = () => {
			window.redirectTracking(href, 1)
		}
	}

	if (!isRedirectTrackingLink && href) { optionals.href = href }

	const className = roomName ? "_gtm-event" : ""

	return html`
		<a
			key=${`${label}-${roomName}`}
			target=${target}
			onClick=${() => {
			if (onClick) { onClick(); generatedOnClick() }
			if (gameRule) { triggerGamificationRule(gameRule, gameRuleCooldown) }
		}}
			data-text=${hoverMessage}
			id=${label}
			data-event-category=${roomName}
			data-event-action=${gaAction}
			data-event-label=${label}
			title=${label}
			aria-label=${label}
			class=${className}
			style="
				width: ${width};
				height: ${height};
				left: ${left};
				top: ${top};
				position:${position};
				cursor: pointer;
				text-decoration: none;
			"
			...${optionals}
		>${children}<//>
		${modal && modalVisible && html`<${Modal} ...${modalOverrides} dismiss=${() => toggleModal(false)}>${modal}<//>`}
		${elfsightPopupID && html`<${ElfsightPopup} ID=${elfsightPopupID} />`}
	`
}

export const AccessBlock = ({ text = "This is an ISF members-only access point", style = {} }) => html`
	<div
		class="tk-access-block"
		style=${{ padding: "25px", backgroundColor: "#1C2545", color: "white", borderRadius: "10px", ...style }}}
	>
		<h1>${text}<//>
		<p><small>Click anywhere outside this popup to close<//><//>
	<//>
`

export const Links = ({ links, roomName }) => 
	html`${links.map(link =>  html`<${Link} ...${link} key=${link.label} roomName=${roomName} />`)}
`

export const MappedImage = ({ imageURL = "", imageFunction = () => "", links = [], imageStyle = "", children = null, roomName = null }) => {
	// useDateTime() refreshes every minute.
	// We don't actually care what the current dateTime is,
	//  we just use this to trigger a redraw of the component
	useDateTime("minute")

	const image = imageURL || imageFunction()

	return html`
		<div key=${image} class="tk-mapped-image" style="position: relative;">
			<${Background} imgURL=${image} style=${imageStyle} />
			<${Links} links=${links} roomName=${roomName} />
			${children}
		</div>
	`
}

export const VimeoFrame = ({ vidId, wrapperStyle = {} }) => html`
<div class="tk-vimeo-frame" style=${{ padding: "30px 30px 24px 30px", borderRadius: "12px", backgroundColor: "#3c1d4a", position: "relative", ...wrapperStyle }}>
  <iframe src="https://player.vimeo.com/video/${vidId}" width="900" height="530" frameborder="0" allow="autoplay; fullscreen" allowfullscreen />
  <span class="tk-close" style="position: absolute; right: 13px; top: 0px; color: white; font-size: 23px;">x<//>
<//>
`

export const SmallVimeoFrame = ({ vidId }) => html`
<div class="tk-vimeo-frame" style="padding: 30px 30px 24px 30px;; border-radius: 12px; background-color: #3c1d4a; position: relative">
  <iframe src="https://player.vimeo.com/video/${vidId}" width="350" height="270" frameborder="0" allow="autoplay; fullscreen" allowfullscreen />
  <span class="tk-close" style="position: absolute; right: 8px; top: 0px; color: white; font-size: 23px;">x<//>
<//>
`

export const YouTube = ({ vidId }) => html`
<div class="tk-youtube-frame" style="padding: 30px 30px 24px 30px;; border-radius: 12px; background-color: #3c1d4a; position: relative">
  <iframe src="https://www.youtube.com/embed/${vidId}" width="560" height="315" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen/>
  <span class="tk-close" style="position: absolute; right: 8px; top: 0px; color: white; font-size: 23px;">x<//>
<//>
`

export const ModalWrapper = ({ children, wrapperStyle = {}, dismissStyle = {} }) => html`
<div class="tk-modal-wrapper" style=${{ padding: "30px 30px 24px 30px", borderRadius: "12px", backgroundColor: "#7536f0", position: "relative", ...wrapperStyle }}>
	${children}
<//>
`

export const SponsorModal = ({ sponsorID }) => html`
	<iframe
		class="tk-sponsor-frame"
		style=${{
		width: "800px",
		height: "600px",
		maxWidth: "75vw",
		maxHeight: "75vh",
		border: "none",
		background: "transparent",
		overflow: "hidden"
	}}
		src="https://isfcongress.tkeventsregistration.com/digital2021/sponsor/${sponsorID}"
	></iframe>
`

export const AllSponsorModal = ({ URL }) => html`
	<iframe
		class="tk-sponsor-frame"
		style=${{
		width: "800px",
		height: "600px",
		maxWidth: "75vw",
		maxHeight: "75vh",
		border: "none",
		background: "transparent",
		overflow: "hidden"
	}}
		src="${URL}"
	></iframe>
`

export const Clue = ({ letter }) => html`
<h1 style="margin: 16px; color: white;" class="game-pop-up-text">Congratulations, you found the letter ${letter}</h1>
`

const getDisplayTime = date => `${date.getHours() < 10 ? "0" : ""}${date.getHours()}:${date.getMinutes() < 10 ? "0" : ""}${date.getMinutes()}`

export const Clock = () => {
	const now = useDateTime()
	return <h1 id="clock" style="height: 5.4%; left: 47.7%; top: 3.6%; position: absolute; color: black; margin: 0;">
		{getDisplayTime(now)}
	</h1>
}

export const BreakoutClock = () => {
	const now = useDateTime()
	return <h1 id="breakout-clock" style="height: 5.4%; left: 49.7%; top: 3.6%; position: absolute; color: black; margin: 0;">
		{getDisplayTime(now)}
	</h1>
}


export const App = ({
	links,
	pageBackgroundURL,
	roomName,
	styles = "",
	before = null,
	extras = null,
	onClick = () => null,
}) => html`
	<div class="tk-before-room" style="z-index: 5;">${before}<//>
	<div class="tk-room" onClick=${onClick} style="position: relative; ${styles}">
		<${Background} imgURL=${pageBackgroundURL} />
		<${Links} links=${links} roomName=${roomName} />
		${extras}
	<//>
`
